import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { cn } from "@/lib/utils";
import { getPageNumbers } from "../utils";

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  visibleItems: number;
  pageSizes?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  selectedCount?: number;
  showSelectedCount?: boolean;
  className?: string;
}

interface PageButtonProps {
  page: number | "...";
  currentPage: number;
  onClick: (page: number) => void;
}

function PageButton({ page, currentPage, onClick }: PageButtonProps) {
  if (page === "...") {
    return (
      <span className="aero:px-2 aero:py-2 aero:text-neutral-400 aero:text-base aero:leading-5">
        ...
      </span>
    );
  }

  const isActive = page === currentPage;

  return (
    <button
      type="button"
      onClick={() => onClick(page)}
      className={cn(
        "aero:min-w-[39px] aero:h-9 aero:rounded aero:border aero:px-4 aero:py-2",
        "aero:text-base aero:font-normal aero:leading-5",
        "aero:transition-colors aero:cursor-pointer",
        isActive
          ? "aero:bg-brand-primary-50 aero:border-brand-primary-700 aero:text-foreground"
          : "aero:bg-white aero:border-neutral-300 aero:text-foreground aero:hover:bg-neutral-50",
      )}
    >
      {page}
    </button>
  );
}

export function DataTablePagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  visibleItems: _visibleItems,
  pageSizes = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  selectedCount = 0,
  showSelectedCount = true,
  className,
}: TablePaginationProps) {
  const pageNumbers = getPageNumbers(totalPages, currentPage);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div
      className={cn(
        "aero:flex aero:items-center aero:justify-between aero:p-3 aero:h-[60px]",
        "aero:bg-white",
        className,
      )}
    >
      {/* Left side - Dropdown and entries info */}
      <div className="aero:flex aero:items-center aero:gap-4 aero:h-9">
        {showSelectedCount && selectedCount > 0 && (
          <span className="aero:font-medium aero:text-brand-primary-600 aero:text-base aero:leading-5">
            {selectedCount} selected
          </span>
        )}
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="aero:h-9 aero:w-[90px] aero:rounded aero:border-neutral-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="aero:text-base aero:font-normal aero:leading-5 aero:text-foreground">
          {_visibleItems} of {totalItems} entries
        </span>
      </div>

      {/* Right side - Pagination controls */}
      <div className="aero:flex aero:items-center aero:gap-6 aero:h-9">
        {/* Previous button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrevious}
          className={cn(
            "aero:flex aero:items-center aero:gap-1 aero:h-5",
            "aero:text-base aero:font-normal aero:leading-5",
            canGoPrevious
              ? "aero:text-neutral-500 aero:cursor-pointer aero:hover:text-neutral-700"
              : "aero:text-neutral-300 aero:cursor-not-allowed",
          )}
        >
          <ChevronLeft className="aero:h-5 aero:w-5" />
          <span>Previous</span>
        </button>

        {/* Page number buttons */}
        <div className="aero:flex aero:items-center aero:gap-2">
          {pageNumbers.map((page, index) => (
            <PageButton
              key={`${page}-${index}`}
              page={page}
              currentPage={currentPage}
              onClick={onPageChange}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext}
          className={cn(
            "aero:flex aero:items-center aero:gap-1 aero:h-5",
            "aero:text-base aero:font-normal aero:leading-5",
            canGoNext
              ? "aero:text-neutral-500 aero:cursor-pointer aero:hover:text-neutral-700"
              : "aero:text-neutral-300 aero:cursor-not-allowed",
          )}
        >
          <span>Next</span>
          <ChevronRight className="aero:h-5 aero:w-5" />
        </button>
      </div>
    </div>
  );
}

interface CompactPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function CompactPagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: CompactPaginationProps) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div className={cn("aero:flex aero:items-center aero:gap-2", className)}>
      <Button
        intent="secondary"
        design="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={!canGoPrevious}
      >
        Previous
      </Button>
      <span className="aero:text-sm aero:text-neutral-600">
        Page {currentPage} of {totalPages}
      </span>
      <Button
        intent="secondary"
        design="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!canGoNext}
      >
        Next
      </Button>
    </div>
  );
}
